import { I18n } from '@calendly/i18n';
import { Table, Header, HeaderCell, Body, Row, Cell, EmptyState as EmptyStateContainer, BatchCheckbox } from '@calendly/table';
import { BareButton } from '@calendly/ui/components/bare-button';
import { FullStoryMask, FullStoryExclude } from '@calendly/ui/components/fullstory-privacy';
import { Link } from '@calendly/ui/components/link';
import { BodyText } from '@calendly/ui/components/typography';
import { VisuallyHidden } from '@calendly/ui/components/visually-hidden';
import { CheckboxField } from '@calendly/ui/form/checkbox-field';
import { useDeviceType } from '@calendly/ui/hooks/use-device-type';
import classNames from 'classnames';
import { forwardRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link as RouterLink } from 'react-router-dom';
import { CalendarsStatus } from 'components/calendars_status';
import { NoGroup } from 'components/core/no_group';
import { MembershipRole } from 'components/core/role';
import { ShowOnly } from 'components/core/show_only';
import { isUserGroupsEnabledSelector } from 'features/user_groups';
import { UserInfoAvatar } from 'features/user_profiles';
import { useMeetingTypesPath } from 'hooks/use_meeting_types_path';
import { idSelector, isAdminOrOwnerSelector } from 'selectors/current_user';
import { ActionsDropdown } from './actions_dropdown';
import { AddToGroupPopup } from './add_to_group_popup';
import { RemovePopup } from './remove_popup';
import { batchCheckboxStatusSelector, sortFieldSelector, sortOrderSelector } from './selectors';
import { sortUsers, togglePageSelection, toggleSelection } from './slice';
const membershipIsTabletDownCss = "m12xa20b";
const membershipCss = "mk45367";
const Membership = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  const isTabletDown = useDeviceType('isTabletDown');
  return <Component ref={ref} {...restProps} className={classNames(membershipCss, {
    [membershipIsTabletDownCss]: isTabletDown
  }, props.className)} />;
});
const emailCss = "e1uifqud";
const Email = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(emailCss, props.className)} />;
});
const eventTypesLinkCss = "e1vu23ee";
const EventTypesLink = forwardRef((props, ref) => <BareButton ref={ref} {...props} className={classNames(eventTypesLinkCss, props.className)} />);
const MemberName = ({
  membership
}) => {
  const isAdminOrOwner = useSelector(isAdminOrOwnerSelector);
  const meetingTypesPath = useMeetingTypesPath(`user/${membership.user_id}`);
  return isAdminOrOwner ? <EventTypesLink as={RouterLink} to={meetingTypesPath}>
        {membership.name}
      </EventTypesLink> : membership.name;
};
const EmptyState = () => <EmptyStateContainer>
    <BodyText weight="bold">{I18n.t('publisher.organization.empty.search')}</BodyText>
    <BodyText>{I18n.t('publisher.organization.empty.search_note_active')}</BodyText>
  </EmptyStateContainer>;

// i18n-tasks-use t('publisher.organization.membership_role.admin')
// i18n-tasks-use t('publisher.organization.membership_role.user')
export const ActiveUsersTable = ({
  memberships
}) => {
  const dispatch = useDispatch();
  const toggleMembership = membership => dispatch(toggleSelection(membership));
  const toggleUsers = () => dispatch(togglePageSelection({
    scope: 'users'
  }));
  const batchCheckboxStatus = useSelector(batchCheckboxStatusSelector);
  const isAdminOrOwner = useSelector(isAdminOrOwnerSelector);
  const currentUserId = useSelector(idSelector);
  const isUserGroupsEnabled = useSelector(isUserGroupsEnabledSelector);
  const sortOrder = useSelector(sortOrderSelector);
  const sortField = useSelector(sortFieldSelector);
  const hasDisabledActions = membership => membership.role === 'owner' || currentUserId === membership.user_id;
  const sortedColumn = {
    name: sortField,
    direction: sortOrder
  };
  return <div data-component="active-users">
      <Table sortedColumn={sortedColumn} onSort={column => dispatch(sortUsers(column))}>
        <Header>
          {isAdminOrOwner && <ShowOnly on="isDesktopUp">
              <HeaderCell hugContent>
                <BatchCheckbox status={batchCheckboxStatus} onChange={toggleUsers} />
              </HeaderCell>
            </ShowOnly>}
          <HeaderCell columnName="name">
            {I18n.t('publisher.organization.headers.name')}
          </HeaderCell>
          <HeaderCell hugContent columnName="human_role">
            {I18n.t('publisher.organization.headers.role')}
          </HeaderCell>
          <HeaderCell>
            {I18n.t('publisher.organization.headers.page')}
          </HeaderCell>
          {isUserGroupsEnabled && <HeaderCell columnName="user_group_name">
            {I18n.t('publisher.organization.headers.group')}
          </HeaderCell>}
          {isAdminOrOwner && <HeaderCell hugContent>
            {I18n.t('publisher.admin.people.headers.calendar')}
          </HeaderCell>}
          <HeaderCell hugContent>
            <VisuallyHidden>{I18n.t('publisher.components.table.action_menu')}</VisuallyHidden>
          </HeaderCell>
        </Header>
        <Body>
          {memberships.map(membership => <Row key={membership.id}>
              {isAdminOrOwner && <ShowOnly on="isDesktopUp">
                  <Cell>
                    <CheckboxField checked={membership.is_selected} onChange={() => toggleMembership(membership)} disabled={hasDisabledActions(membership)} aria-label={I18n.t('publisher.components.table.select_item', {
                item: membership.name
              })} />
                  </Cell>
                </ShowOnly>}
              <Cell>
                <Membership>
                  <FullStoryExclude>
                    <UserInfoAvatar uuid={membership.user_uuid} letter={membership.avatar_letter} src={membership.avatar_url} size="big" name={membership.name} spacingRight={8} />
                  </FullStoryExclude>
                  <div>
                    <FullStoryMask>
                      <MemberName membership={membership} />
                    </FullStoryMask>
                    <FullStoryMask>
                      <Email>{membership.email}</Email>
                    </FullStoryMask>
                  </div>
                </Membership>
              </Cell>
              <Cell label={I18n.t('publisher.organization.headers.role')}>
                <FullStoryMask>
                  <MembershipRole membershipRole={membership.role} />
                </FullStoryMask>
              </Cell>
              <Cell label={I18n.t('publisher.organization.headers.page')}>
                <FullStoryMask>
                  <Link href={membership.booking_url}>{membership.human_booking_url}</Link>
                </FullStoryMask>
              </Cell>
              {isUserGroupsEnabled && <Cell label={I18n.t('publisher.organization.headers.group')}>
                <FullStoryMask>
                  {membership.user_group_membership && membership.user_group_membership.group_name || <NoGroup />}
                </FullStoryMask>
              </Cell>}
              {isAdminOrOwner && <Cell label={I18n.t('publisher.organization.headers.calendar_connected')}>
                <FullStoryMask>
                  <CalendarsStatus calendarAccounts={membership.calendar_accounts || []} name={membership.name} />
                </FullStoryMask>
              </Cell>}
              <Cell hasActions>
                {isAdminOrOwner && !hasDisabledActions(membership) && <FullStoryExclude>
                    <ActionsDropdown membership={membership} />
                  </FullStoryExclude>}
              </Cell>
            </Row>)}
          {memberships.length === 0 && <EmptyState />}
        </Body>
      </Table>
      <RemovePopup />
      <AddToGroupPopup />
    </div>;
};

require("./table.wyw-in-js.css!=!../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./table.js");